import axios from 'axios';
import { ChatAttachment, ChatConversation, ChatMember, ChatMessage } from '../types';

const API = `${process.env.REACT_APP_API_URL}`;

export interface Paginated<T> {
  results: T[];
  page: number;
  limit: number;
  totalResults: number;
  totalPage: number;
}

export const chatApi = {
  getConversations: async (): Promise<ChatConversation[]> => (await axios.get(`${API}/chat/conversations`)).data,

  createDm: async (userId: number): Promise<ChatConversation> =>
    (await axios.post(`${API}/chat/conversations/dm`, { userId })).data,

  createChannel: async (body: {
    name: string;
    description?: string;
    isPrivate?: boolean;
    memberIds?: number[];
  }): Promise<ChatConversation> => (await axios.post(`${API}/chat/channels`, body)).data,

  getMessages: async (
    conversationId: number,
    params?: { before?: number; limit?: number },
  ): Promise<{ results: ChatMessage[]; hasMore: boolean }> =>
    (await axios.get(`${API}/chat/conversations/${conversationId}/messages`, { params })).data,

  createMessage: async (
    conversationId: number,
    body: { content?: string; attachments?: ChatAttachment[]; replyToId?: number | null },
  ): Promise<ChatMessage> => (await axios.post(`${API}/chat/conversations/${conversationId}/messages`, body)).data,

  editMessage: async (messageId: number, content: string): Promise<ChatMessage> =>
    (await axios.patch(`${API}/chat/messages/${messageId}`, { content })).data,

  deleteMessage: async (messageId: number): Promise<{ id: number; conversationId: number }> =>
    (await axios.delete(`${API}/chat/messages/${messageId}`)).data,

  addReaction: async (messageId: number, emoji: string) =>
    (await axios.post(`${API}/chat/messages/${messageId}/reactions`, { emoji })).data,

  removeReaction: async (messageId: number, emoji: string) =>
    (await axios.delete(`${API}/chat/messages/${messageId}/reactions`, { data: { emoji } })).data,

  markRead: async (conversationId: number, lastReadMessageId: number) =>
    (await axios.post(`${API}/chat/conversations/${conversationId}/read`, { lastReadMessageId })).data,

  getMembers: async (conversationId: number): Promise<ChatMember[]> =>
    (await axios.get(`${API}/chat/conversations/${conversationId}/members`)).data,

  addMembers: async (conversationId: number, userIds: number[]) =>
    (await axios.post(`${API}/chat/conversations/${conversationId}/members`, { userIds })).data,

  removeMember: async (conversationId: number, userId: number) =>
    (await axios.delete(`${API}/chat/conversations/${conversationId}/members/${userId}`)).data,

  getContacts: async (params?: { page?: number; limit?: number; search?: string }): Promise<Paginated<ChatUserResult>> =>
    (await axios.get(`${API}/chat/contacts`, { params })).data,

  search: async (q: string): Promise<{ messages: any[]; conversations: any[] }> =>
    (await axios.get(`${API}/chat/search`, { params: { q } })).data,

  // Ekler: mevcut /upload (OneDrive). Sunucu yalnız URL döner; isim/mime/size'ı File'dan alırız.
  uploadFiles: async (files: File[]): Promise<ChatAttachment[]> => {
    const form = new FormData();
    files.forEach((f) => form.append('images', f));
    const res = await axios.post(`${API}/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    const urls: string[] = Array.isArray(res.data) ? res.data : [res.data];
    return files.map((f, i) => ({ url: urls[i], name: f.name, mime: f.type, size: f.size }));
  },
};

export interface ChatUserResult {
  id: number;
  name: string;
  surname: string;
  email: string;
  lastActivityAt?: string | null;
}
